import React, { useCallback, useContext, useEffect } from 'react';
import { compose, withApollo } from 'react-apollo';
import { withRouter } from 'react-router';
import isEmpty from 'lodash/isEmpty';
import LoadingSpinner from 'chameleon/packages/blue-book-player/LoadingSpinner';
import {
  BluebookContext,
  withBluebookContext,
} from 'chameleon/packages/blue-book-player/state/BluebookContext';
import {
  SET_IS_REVIEW_PAGE,
  SET_PLAYER,
} from 'chameleon/packages/blue-book-player/state/reducer';
import { connect } from 'react-redux';
import CharmBracelet from 'chameleon/legacy/packages/lrn-player/src/modules/Player/components/CharmBracelet';
import withLearnosityConfig from 'chameleon/packages/blue-book-player/hocs/withLearnosityConfig';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import useFirstLoad from 'chameleon/packages/blue-book-player/hooks/useFirstLoad';
import withBlueBookTeacherPlayer from './hocs/withBlueBookTeacherPlayer';
import QuestionDetails from './QuestionDetails';

const TeacherPlayer = (props) => {
  const {
    itemId,
    assessment,
    learnosityServerData,
    allowCalculatorForItems,
    subject,
    learnosityApp,
    setAreDetailsExpanded,
    areDetailsExpanded,
    userData,
    isReordering,
    onOrderChange,
    getCurrentQuestionIndex,
    onQuestionChanged,
  } = props;
  const { state: playerState, dispatch } = useContext(BluebookContext);
  const playerEl = useGetPlayerEl();

  useEffect(() => {
    if (!isEmpty(assessment)) {
      const { questions, title } = assessment;
      dispatch({
        type: SET_PLAYER,
        payload: {
          questions,
          title,
          learnositySignedRequest: learnosityServerData,
          allowCalculatorForItems,
          isApcDataLoading: false,
        },
      });
    }
  }, [assessment, learnosityServerData, allowCalculatorForItems, dispatch]);

  useEffect(() => {
    const setCurrentQuestionDetails = (itemIndex) => {
      getCurrentQuestionIndex(itemIndex);
      const currentQuestion = assessment.questions[itemIndex];
      onQuestionChanged(currentQuestion);
    };

    if (playerState?.isTestReady && getCurrentQuestionIndex) {
      learnosityApp?.on('item:changed', setCurrentQuestionDetails);
    }
    return () => {
      learnosityApp?.off('item:changed', setCurrentQuestionDetails);
    };
  }, [
    playerState?.isTestReady,
    getCurrentQuestionIndex,
    onQuestionChanged,
    assessment?.questions,
  ]);

  const setCharmBraceletReorderingHeight = () => {
    if (!isReordering) return;
    const charmBraceletReorderingElement = document.querySelector(
      '.question-list.all-overview-icons',
    );

    if (playerEl && charmBraceletReorderingElement) {
      const charmBraceletHeight = charmBraceletReorderingElement?.offsetHeight;
      playerEl.style.cssText = `--charm-bracelet-height: ${charmBraceletHeight || '0'}px;`;
    }
  };

  const removeInLineStyles = () => {
    if (playerEl) {
      playerEl.style.cssText = '';
    }
  };

  useEffect(() => {
    if (!isReordering) {
      removeInLineStyles();
      return;
    }
    setCharmBraceletReorderingHeight();
  }, [isReordering, playerEl]);

  useEffect(() => {
    playerEl?.classList?.add('with-charm-bracelet');
  });

  useFirstLoad(playerState, setCharmBraceletReorderingHeight);
  const handleOnItemChanged = useCallback(() => {
    if (playerState.isReviewPage) {
      dispatch({
        type: SET_IS_REVIEW_PAGE,
        payload: false,
      });
    }
  }, [playerState.isReviewPage, dispatch]);

  return !playerState?.isTestReady ? (
    <LoadingSpinner />
  ) : (
    <>
      <CharmBracelet
        learnosityData={{
          app: learnosityApp,
          items: learnosityApp.getItems(),
          activity: learnosityApp.getActivity(),
        }}
        showNavigationNumbers={false}
        showDoubleArrow
        onOrderChange={onOrderChange}
        isReordering={isReordering}
        onItemChanged={handleOnItemChanged}
        preview
      />
      <QuestionDetails
        learnosityServerData={learnosityServerData}
        questions={assessment.questions}
        allowCalculatorForItems={allowCalculatorForItems}
        itemId={itemId}
        subject={subject}
        learnosityApp={learnosityApp}
        userData={userData}
        setAreDetailsExpanded={setAreDetailsExpanded}
        areDetailsExpanded={areDetailsExpanded}
      />
    </>
  );
};

export default compose(
  withApollo,
  withRouter,
  connect(({ subject: { data }, userData }) => ({
    subject: data,
    userData,
  })),
  withLearnosityConfig({ isBlueBook: true }),
  withBluebookContext,
  withBlueBookTeacherPlayer,
)(TeacherPlayer);
